import { useCallback, useState } from "react";
import { getAuto4clientLogStreams, getScraperLogStreams } from "../../../services/aws/cloudwatch";
import { getErrorList, getTaskLogs } from "../../../services/mongo/taskLogs";

/**
 * Formats a log stream object by adding id, timestamp and link properties
 * @param {Object} stream - The raw log stream data
 * @returns {Object} Formatted log stream with additional properties
 */
const formatLogStream = (stream) => ({
  ...stream,
  id: stream.arn,
  creationTime: new Date(stream.creationTime).toLocaleString(),
  link: "1",
});

/**
 * Formats a MongoDB task log by adding id and link properties
 * @param {Object} log - The raw MongoDB task log
 * @returns {Object} Formatted task log with additional properties
 */
const formatMongoTaskLog = (log) => ({
  ...log,
  id: log["Task ARN"],
  link: "1",
});

/**
 * Formats an error object by adding id and link properties
 * @param {Object} error - The raw error data
 * @returns {Object} Formatted error with additional properties
 */
const formatError = (error) => ({
  ...error,
  id: error["_id"],
  link: "1",
});

/**
 * Custom hook for managing and fetching different types of logs
 * @returns {Object} Object containing logs data and fetch functions
 */
export const useLogs = () => {
  // Initialize state object to store different types of logs
  const [logs, setLogs] = useState({
    mongoTaskLogs: [], // Logs from MongoDB tasks
    scraperLogStreams: [], // Logs from ECS scraper tasks
    auto4clientLogStreams: [], // Logs from SageMaker tasks
    errorList: [], // Collection of error logs
  });

  // Fetch mongo task logs for all tasks
  const fetchMongoTaskLogs = useCallback(async () => {
    const response = await getTaskLogs();
    if (response.status !== 200) {
      return;
    }
    const data = await response.json();
    const formattedLogs = data.task_logs.map(formatMongoTaskLog);
    setLogs((prev) => ({ ...prev, mongoTaskLogs: formattedLogs }));
  }, []);

  // Fetch scraper log streams for tasks that were run on ecs
  const fetchScraperLogStreams = useCallback(async () => {
    const response = await getScraperLogStreams();
    if (response.status !== 200) {
      return;
    }
    const data = await response.json();
    const formattedLogs = data.log_streams.map(formatLogStream);
    setLogs((prev) => ({ ...prev, scraperLogStreams: formattedLogs }));
  }, []);

  // Fetch auto4client log streams for tasks that were run on sagemaker
  const fetchAuto4clientLogStreams = useCallback(async () => {
    const logStreamsRes = await getAuto4clientLogStreams();
    if (logStreamsRes.status !== 200) {
      return;
    }
    const logStreamsJson = await logStreamsRes.json();
    const logStreamsWithId = logStreamsJson.log_streams.map(formatLogStream);
    setLogs((prev) => ({ ...prev, auto4clientLogStreams: logStreamsWithId }));
  }, []);

  // Fetch error list
  const fetchErrorList = useCallback(async () => {
    const errorListRes = await getErrorList();
    if (errorListRes.status !== 200) {
      return;
    }
    const errorListJson = await errorListRes.json();
    const errorListWithId = errorListJson.scraper_errors.map(formatError);
    setLogs((prev) => ({ ...prev, errorList: errorListWithId }));
  }, []);

  // Combined fetch function
  const fetchAllLogs = useCallback(async () => {
    await Promise.all([
      fetchMongoTaskLogs(),
      fetchScraperLogStreams(),
      fetchAuto4clientLogStreams(),
      fetchErrorList(),
    ]);
  }, [fetchMongoTaskLogs, fetchScraperLogStreams, fetchAuto4clientLogStreams, fetchErrorList]);

  // Return logs data and fetch functions for use in components
  return {
    logs,
    fetchAllLogs,
    fetchMongoTaskLogs,
    fetchScraperLogStreams,
    fetchAuto4clientLogStreams,
    fetchErrorList,
  };
};
